import { useState } from 'react'

import { KeyboardDoubleArrowDown } from '@mui/icons-material'
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'

import {
  AddressBookingDto,
  Booking,
  BookingAddressType,
  BookingStatus,
  BookingTrackingDto,
} from 'generated/graphql'
import { formatDeliveryTime } from 'helpers'
import { useBookingTrackingData, useBookingEta, useAllAddressesEtaByTrackingHash } from 'hooks'
import useBookingAddresses from 'hooks/booking/useBookingAddresses'

import { TrackingSectionContent } from './TrackingSectionContent'
import {
  DeliveryTimeEstimate,
  DetailedView,
  BookingTrackingMap,
  ExpiredTrackingMessage,
  TrackingNotFoundMessage,
  ProofOfDeliveryView,
} from './ui'
import { useAvailableHeight } from './useAvailableHeight'

const getFirstUnsignedAddress = (
  referenceAddressId: any,
  referenceAddress: AddressBookingDto | undefined | null,
  bookingData: BookingTrackingDto | undefined | null,
) => {
  if (referenceAddressId) {
    return referenceAddress?.signedAt ? null : referenceAddress
  }

  return (
    (bookingData?.addresses ?? [])
      .slice()
      .sort((a: any, b: any) => a.sequenceOrder - b.sequenceOrder)
      .find((a: any) => !a.signedAt) ?? null
  )
}

interface MobileTrackingActiveContentProps {
  showDetailedView: boolean
  showProofOfCollection: boolean
  showProofOfDelivery: boolean
  showCombinedDocs: boolean
  isDelivered: boolean
  bookingData: any
  bookingCurrentAction: any
  nextBookingAction: any
  consignmentPairs: any[]
  deliveryCompletedAt?: string
  isMobile: boolean
  nextAddressEta?: string | null
  etaWindowMinutes?: number | null
  etaLoading?: boolean
  onDetailsClick: () => void
  onDetailsClose: () => void
  onProofOfDeliveryClick: () => void
  onProofOfCollectionClick: () => void
  onCombinedDocsClick: () => void
  referenceAddressType: BookingAddressType | null
  collectionAttachmentCount: number
  deliveryAttachmentCount: number
  combinedDocAttachmentCount: number
  addressEtas: any[]
  collectionAttachments: any[]
  deliveryAttachments: any[]
  combinedDocAttachments: any[]
  talkToAnExpertUrl: string
}

const MobileTrackingActiveContent = ({
  showDetailedView,
  showProofOfCollection,
  showProofOfDelivery,
  showCombinedDocs,
  isDelivered,
  bookingData,
  bookingCurrentAction,
  nextBookingAction,
  consignmentPairs,
  deliveryCompletedAt,
  isMobile,
  nextAddressEta,
  etaWindowMinutes,
  etaLoading,
  onDetailsClick,
  onDetailsClose,
  onProofOfDeliveryClick,
  onProofOfCollectionClick,
  onCombinedDocsClick,
  referenceAddressType,
  collectionAttachmentCount,
  deliveryAttachmentCount,
  combinedDocAttachmentCount,
  addressEtas,
  collectionAttachments,
  deliveryAttachments,
  combinedDocAttachments,
  talkToAnExpertUrl,
}: MobileTrackingActiveContentProps) => {
  if (showDetailedView) {
    return (
      <DetailedView
        onClose={onDetailsClose}
        bookingCurrentAction={bookingCurrentAction}
        nextBookingAction={nextBookingAction}
        consignmentPairs={consignmentPairs}
      />
    )
  }

  if (showProofOfCollection) {
    return (
      <ProofOfDeliveryView
        mode='collection'
        onClose={onProofOfCollectionClick}
        booking={bookingData}
        attachments={collectionAttachments}
      />
    )
  }

  if (showProofOfDelivery) {
    return (
      <ProofOfDeliveryView
        mode='delivery'
        onClose={onProofOfDeliveryClick}
        booking={bookingData}
        attachments={deliveryAttachments}
      />
    )
  }

  if (showCombinedDocs) {
    return (
      <ProofOfDeliveryView
        mode='combined'
        onClose={onCombinedDocsClick}
        booking={bookingData}
        attachments={combinedDocAttachments}
      />
    )
  }

  return (
    <TrackingSectionContent
      isDelivered={isDelivered}
      bookingData={bookingData}
      talkToAnExpertUrl={talkToAnExpertUrl}
      bookingCurrentAction={bookingCurrentAction}
      nextBookingAction={nextBookingAction}
      consignmentPairs={consignmentPairs}
      deliveryCompletedAt={deliveryCompletedAt}
      isMobile={isMobile}
      nextAddressEta={nextAddressEta}
      etaWindowMinutes={etaWindowMinutes}
      etaLoading={etaLoading}
      onDetailsClick={onDetailsClick}
      onProofOfDeliveryClick={onProofOfDeliveryClick}
      onProofOfCollectionClick={onProofOfCollectionClick}
      referenceAddressType={referenceAddressType}
      collectionAttachmentCount={collectionAttachmentCount}
      deliveryAttachmentCount={deliveryAttachmentCount}
      combinedDocAttachmentCount={combinedDocAttachmentCount}
      onCombinedDocsClick={onCombinedDocsClick}
      addressEtas={addressEtas}
    />
  )
}

interface BookingTrackingMobileViewProps {
  bookingData: any
  showDetailedView: boolean
  showProofOfCollection: boolean
  showProofOfDelivery: boolean
  showCombinedDocs: boolean
  bookingCurrentAction: any
  nextBookingAction: any
  consignmentPairs: any[]
  deliveryCompletedAt?: string
  isMobile: boolean
  onDetailsClick: () => void
  onDetailsClose: () => void
  onProofOfDeliveryClick: () => void
  onProofOfCollectionClick: () => void
  onCombinedDocsClick: () => void
  referenceAddressType: BookingAddressType | null
  collectionAttachmentCount: number
  deliveryAttachmentCount: number
  combinedDocAttachmentCount: number
  addressEtas: any[]
  collectionAttachments: any[]
  deliveryAttachments: any[]
  combinedDocAttachments: any[]
  isDomesticDedicated: boolean
  driversLocations: any[]
  referenceAddressId: any
  isDelivered: boolean
  nextAddressEta?: string | null
  etaWindowMinutes?: number | null
  etaLoading?: boolean
  etaAddressType: 'collection' | 'delivery'
  containerRef: any
  containerStyle: any
  showScrollIndicator: boolean
  setShowScrollIndicator: React.Dispatch<React.SetStateAction<boolean>>
  talkToAnExpertUrl: string
}

interface WebTrackingActiveContentProps {
  showDetailedView: boolean
  showProofOfCollection: boolean
  showProofOfDelivery: boolean
  showCombinedDocs: boolean
  isDelivered: boolean
  bookingData: any
  bookingCurrentAction: any
  nextBookingAction: any
  consignmentPairs: any[]
  deliveryCompletedAt?: string
  isMobile: boolean
  nextAddressEta?: string | null
  etaWindowMinutes?: number | null
  etaLoading?: boolean
  onDetailsClick: () => void
  collectionAttachments: any[]
  deliveryAttachments: any[]
  combinedDocAttachments: any[]
  referenceAddressType: BookingAddressType | null
  collectionAttachmentCount: number
  deliveryAttachmentCount: number
  combinedDocAttachmentCount: number
  addressEtas: any[]
  onDetailsClose: () => void
  onProofOfCollectionClick: () => void
  onProofOfDeliveryClick: () => void
  onCombinedDocsClick: () => void
  talkToAnExpertUrl: string
}

const WebTrackingActiveContent = ({
  showDetailedView,
  showProofOfCollection,
  showProofOfDelivery,
  showCombinedDocs,
  isDelivered,
  bookingData,
  bookingCurrentAction,
  nextBookingAction,
  consignmentPairs,
  deliveryCompletedAt,
  isMobile,
  nextAddressEta,
  etaWindowMinutes,
  etaLoading,
  onDetailsClick,
  collectionAttachments,
  deliveryAttachments,
  combinedDocAttachments,
  referenceAddressType,
  collectionAttachmentCount,
  deliveryAttachmentCount,
  combinedDocAttachmentCount,
  addressEtas,
  onDetailsClose,
  onProofOfCollectionClick,
  onProofOfDeliveryClick,
  onCombinedDocsClick,
  talkToAnExpertUrl,
}: WebTrackingActiveContentProps) => {
  if (showDetailedView) {
    return (
      <DetailedView
        onClose={onDetailsClose}
        bookingCurrentAction={bookingCurrentAction}
        nextBookingAction={nextBookingAction}
        consignmentPairs={consignmentPairs}
      />
    )
  }

  if (showProofOfCollection) {
    return (
      <ProofOfDeliveryView
        mode='collection'
        onClose={onProofOfCollectionClick}
        booking={bookingData}
        attachments={collectionAttachments}
      />
    )
  }

  if (showProofOfDelivery) {
    return (
      <ProofOfDeliveryView
        mode='delivery'
        onClose={onProofOfDeliveryClick}
        booking={bookingData}
        attachments={deliveryAttachments}
      />
    )
  }

  if (showCombinedDocs) {
    return (
      <ProofOfDeliveryView
        mode='combined'
        onClose={onCombinedDocsClick}
        booking={bookingData}
        attachments={combinedDocAttachments}
      />
    )
  }

  return (
    <TrackingSectionContent
      isDelivered={isDelivered}
      bookingData={bookingData}
      talkToAnExpertUrl={talkToAnExpertUrl}
      bookingCurrentAction={bookingCurrentAction}
      nextBookingAction={nextBookingAction}
      consignmentPairs={consignmentPairs}
      deliveryCompletedAt={deliveryCompletedAt}
      isMobile={isMobile}
      nextAddressEta={nextAddressEta}
      etaWindowMinutes={etaWindowMinutes}
      etaLoading={etaLoading}
      onDetailsClick={onDetailsClick}
      onProofOfDeliveryClick={onProofOfDeliveryClick}
      onProofOfCollectionClick={onProofOfCollectionClick}
      referenceAddressType={referenceAddressType}
      collectionAttachmentCount={collectionAttachmentCount}
      deliveryAttachmentCount={deliveryAttachmentCount}
      combinedDocAttachmentCount={combinedDocAttachmentCount}
      onCombinedDocsClick={onCombinedDocsClick}
      addressEtas={addressEtas}
    />
  )
}

const BookingTrackingMobileView = ({
  bookingData,
  showDetailedView,
  showProofOfCollection,
  showProofOfDelivery,
  showCombinedDocs,
  bookingCurrentAction,
  nextBookingAction,
  consignmentPairs,
  deliveryCompletedAt,
  isMobile,
  onDetailsClick,
  onDetailsClose,
  onProofOfDeliveryClick,
  onProofOfCollectionClick,
  onCombinedDocsClick,
  referenceAddressType,
  collectionAttachmentCount,
  deliveryAttachmentCount,
  combinedDocAttachmentCount,
  addressEtas,
  collectionAttachments,
  deliveryAttachments,
  combinedDocAttachments,
  isDomesticDedicated,
  driversLocations,
  referenceAddressId,
  isDelivered,
  nextAddressEta,
  etaWindowMinutes,
  etaLoading,
  etaAddressType,
  containerRef,
  containerStyle,
  showScrollIndicator,
  setShowScrollIndicator,
  talkToAnExpertUrl,
}: BookingTrackingMobileViewProps) => {
  return (
    <Box
      ref={containerRef}
      sx={{ ...containerStyle, overflowY: 'auto' }}
      onScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        const scrollable = scrollHeight - clientHeight
        if (scrollable > 0 && scrollTop / scrollable >= 0.9) setShowScrollIndicator(false)
      }}>
      <Box sx={{ height: 'min(600px, 85svh)', width: '100%', position: 'relative' }}>
        <BookingTrackingMap
          bookingDetails={bookingData}
          driverLocations={isDomesticDedicated ? driversLocations : []}
          hideRoute={referenceAddressId != null}
          referenceAddressId={referenceAddressId}
        />
        {!isDelivered && (
          <DeliveryTimeEstimate
            nextAddressEta={nextAddressEta}
            etaWindowMinutes={etaWindowMinutes}
            loading={etaLoading}
            addressType={etaAddressType}
            bookingStatus={bookingData?.status}
          />
        )}
      </Box>

      <MobileTrackingActiveContent
        showDetailedView={showDetailedView}
        showProofOfCollection={showProofOfCollection}
        showProofOfDelivery={showProofOfDelivery}
        showCombinedDocs={showCombinedDocs}
        isDelivered={isDelivered}
        bookingData={bookingData}
        talkToAnExpertUrl={talkToAnExpertUrl}
        bookingCurrentAction={bookingCurrentAction}
        nextBookingAction={nextBookingAction}
        consignmentPairs={consignmentPairs}
        deliveryCompletedAt={deliveryCompletedAt}
        isMobile={isMobile}
        nextAddressEta={nextAddressEta}
        etaWindowMinutes={etaWindowMinutes}
        etaLoading={etaLoading}
        onDetailsClick={onDetailsClick}
        onDetailsClose={onDetailsClose}
        onProofOfDeliveryClick={onProofOfDeliveryClick}
        onProofOfCollectionClick={onProofOfCollectionClick}
        onCombinedDocsClick={onCombinedDocsClick}
        referenceAddressType={referenceAddressType}
        collectionAttachmentCount={collectionAttachmentCount}
        deliveryAttachmentCount={deliveryAttachmentCount}
        combinedDocAttachmentCount={combinedDocAttachmentCount}
        addressEtas={addressEtas}
        collectionAttachments={collectionAttachments}
        deliveryAttachments={deliveryAttachments}
        combinedDocAttachments={combinedDocAttachments}
      />

      {showScrollIndicator && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 4,
            pb: 2,
            background: 'linear-gradient(transparent, white)',
            color: '#333',
            pointerEvents: 'none',
          }}>
          <KeyboardDoubleArrowDown sx={{ fontSize: 24 }} />
          <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '13px', mt: 0.25 }}>
            More delivery details below
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export const BookingTrackingPage = () => {
  const [showDetailedView, setShowDetailedView] = useState(false)
  const [showProofOfDelivery, setShowProofOfDelivery] = useState(false)
  const [showProofOfCollection, setShowProofOfCollection] = useState(false)
  const [showCombinedDocs, setShowCombinedDocs] = useState(false)
  const [showScrollIndicator, setShowScrollIndicator] = useState(true)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { ref: containerRef, style: containerStyle } = useAvailableHeight()

  const {
    trackingHash,
    bookingData,
    talkToAnExpertUrl,
    bookingDataLoading,
    driversLocations,
    isDomesticDedicated,
    isExpired,
    referenceAddressId,
  } = useBookingTrackingData()

  const { nextAddressEta, etaWindowMinutes, etaLoading } = useBookingEta({
    trackingHash,
    pollInterval: 300000,
    skip: !trackingHash,
  })

  const { addressEtas } = useAllAddressesEtaByTrackingHash(trackingHash, 300000)

  const { bookingCurrentAction, nextBookingAction, consignmentPairs } = useBookingAddresses(
    bookingData as unknown as Booking,
    true,
    referenceAddressId,
  )

  const deliveryCompletedAt = formatDeliveryTime(bookingData?.deliveryCompletedAt)

  const referenceAddress = referenceAddressId
    ? (bookingData?.addresses ?? []).find((a: any) => String(a.id) === String(referenceAddressId))
    : null
  const referenceAddressType: BookingAddressType | null = (referenceAddress as any)?.type ?? null

  const firstUnsignedAddress = getFirstUnsignedAddress(
    referenceAddressId,
    referenceAddress,
    bookingData as unknown as BookingTrackingDto,
  )

  const etaAddressType: 'collection' | 'delivery' =
    (firstUnsignedAddress as any)?.type === BookingAddressType.Collection
      ? 'collection'
      : 'delivery'

  const collectionAttachments = (bookingData?.addressAttachments ?? []).filter(
    (a: any) => a.addressType === BookingAddressType.Collection,
  )
  const deliveryAttachments = (bookingData?.addressAttachments ?? []).filter(
    (a: any) => a.addressType === BookingAddressType.Delivery,
  )
  const combinedDocAttachments = bookingData?.bookingAttachments ?? []

  const handleDetailsClick = () => {
    setShowDetailedView(true)
  }

  const handleCloseDetailedView = () => {
    setShowDetailedView(false)
  }

  const handleToggleProofOfDelivery = () => {
    setShowProofOfDelivery((prev) => !prev)
  }

  const handleToggleProofOfCollection = () => {
    setShowProofOfCollection((prev) => !prev)
  }

  const handleToggleCombinedDocs = () => {
    setShowCombinedDocs((prev) => !prev)
  }

  if (bookingDataLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isExpired) {
    return <ExpiredTrackingMessage talkToAnExpertUrl={talkToAnExpertUrl} />
  }

  if (!bookingDataLoading && !bookingData) {
    return <TrackingNotFoundMessage />
  }

  const isDelivered =
    (referenceAddressId != null && Boolean(bookingData?.deliveryCompletedAt)) ||
    bookingData?.status === BookingStatus.ProofOfDelivery ||
    bookingData?.status === BookingStatus.Complete ||
    bookingData?.status === BookingStatus.Invoice

  if (isMobile) {
    return (
      <BookingTrackingMobileView
        bookingData={bookingData}
        talkToAnExpertUrl={talkToAnExpertUrl}
        showDetailedView={showDetailedView}
        showProofOfCollection={showProofOfCollection}
        showProofOfDelivery={showProofOfDelivery}
        showCombinedDocs={showCombinedDocs}
        bookingCurrentAction={bookingCurrentAction}
        nextBookingAction={nextBookingAction}
        consignmentPairs={consignmentPairs}
        deliveryCompletedAt={deliveryCompletedAt}
        isMobile={isMobile}
        onDetailsClick={handleDetailsClick}
        onDetailsClose={handleCloseDetailedView}
        onProofOfDeliveryClick={handleToggleProofOfDelivery}
        onProofOfCollectionClick={handleToggleProofOfCollection}
        onCombinedDocsClick={handleToggleCombinedDocs}
        referenceAddressType={referenceAddressType}
        collectionAttachmentCount={collectionAttachments.length}
        deliveryAttachmentCount={deliveryAttachments.length}
        combinedDocAttachmentCount={combinedDocAttachments.length}
        addressEtas={addressEtas}
        collectionAttachments={collectionAttachments}
        deliveryAttachments={deliveryAttachments}
        combinedDocAttachments={combinedDocAttachments}
        isDomesticDedicated={isDomesticDedicated}
        driversLocations={driversLocations}
        referenceAddressId={referenceAddressId}
        isDelivered={isDelivered}
        nextAddressEta={nextAddressEta}
        etaWindowMinutes={etaWindowMinutes}
        etaLoading={etaLoading}
        etaAddressType={etaAddressType}
        containerRef={containerRef}
        containerStyle={containerStyle}
        showScrollIndicator={showScrollIndicator}
        setShowScrollIndicator={setShowScrollIndicator}
      />
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        ...containerStyle,
        display: 'flex',
      }}>
      <Box
        sx={{
          width: '600px',
          minWidth: '600px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
        }}>
        <WebTrackingActiveContent
          showDetailedView={showDetailedView}
          showProofOfCollection={showProofOfCollection}
          showProofOfDelivery={showProofOfDelivery}
          showCombinedDocs={showCombinedDocs}
          isDelivered={isDelivered}
          bookingData={bookingData}
          talkToAnExpertUrl={talkToAnExpertUrl}
          bookingCurrentAction={bookingCurrentAction}
          nextBookingAction={nextBookingAction}
          consignmentPairs={consignmentPairs}
          deliveryCompletedAt={deliveryCompletedAt}
          isMobile={isMobile}
          nextAddressEta={nextAddressEta}
          etaWindowMinutes={etaWindowMinutes}
          etaLoading={etaLoading}
          onDetailsClick={handleDetailsClick}
          collectionAttachments={collectionAttachments}
          deliveryAttachments={deliveryAttachments}
          combinedDocAttachments={combinedDocAttachments}
          referenceAddressType={referenceAddressType}
          collectionAttachmentCount={collectionAttachments.length}
          deliveryAttachmentCount={deliveryAttachments.length}
          combinedDocAttachmentCount={combinedDocAttachments.length}
          addressEtas={addressEtas}
          onDetailsClose={handleCloseDetailedView}
          onProofOfCollectionClick={handleToggleProofOfCollection}
          onProofOfDeliveryClick={handleToggleProofOfDelivery}
          onCombinedDocsClick={handleToggleCombinedDocs}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          height: '100%',
          position: 'relative',
        }}>
        <BookingTrackingMap
          bookingDetails={bookingData!}
          driverLocations={isDomesticDedicated ? driversLocations : []}
          hideRoute={referenceAddressId != null}
          referenceAddressId={referenceAddressId}
        />
      </Box>
    </Box>
  )
}
